import type { PreflightOrderDraft } from "@/lib/preflightOrderDraft";
import {
  clearPreflightDraft,
  loadPreflightDraft,
  savePreflightDraft,
} from "@/lib/preflightOrderDraft";
import type { CheckoutFormData } from "@/lib/validations/checkout";
import type { FlightPassengerContextV1 } from "@/lib/checkoutFlightContext";
import { buildAuthPath } from "@/lib/authPaths";
import { appendUtmToPath } from "@/lib/utmAttribution";

export const GUEST_FIRST_ORDER_STORAGE_KEY = "pfa_guest_first_order_v1";
export const GUEST_FIRST_ORDER_INTENT = "first-order" as const;
export const GUEST_CHECKOUT_PATH = "/CFA/checkout/guest";
/** Guest browse → basket → flight checkout → signup that places the saved order. */
export const SEARCH_CATERERS_FIRST_ORDER_PATH = `/CFA/search-caterers?intent=${GUEST_FIRST_ORDER_INTENT}`;
export const ONBOARDING_COMPLETE_FIRST_ORDER_PATH =
  "/onboarding?role=pfa&complete_first_order=1";
/** Post-signup onboarding for marketing Order now (account first, order later). */
export const ORDER_NOW_ONBOARDING_PATH = "/onboarding?role=pfa";
export const SEARCH_CATERERS_MAP_PATH = "/CFA/search-caterers";

/** Last airport the user browsed for caterers (search map / airport list). */
export const BROWSE_SERVICING_AIRPORT_KEY = "pfa_browse_servicing_airport_v1";

export function setBrowseServicingAirportId(airportId: string | null): void {
  try {
    if (!airportId?.trim()) {
      sessionStorage.removeItem(BROWSE_SERVICING_AIRPORT_KEY);
      return;
    }
    sessionStorage.setItem(BROWSE_SERVICING_AIRPORT_KEY, airportId.trim());
  } catch {
    /* ignore */
  }
}

export function getBrowseServicingAirportId(): string | null {
  try {
    const id = sessionStorage.getItem(BROWSE_SERVICING_AIRPORT_KEY)?.trim();
    return id || null;
  } catch {
    return null;
  }
}

/**
 * Servicing airport from search (`?airport=`) or the last browsed airport.
 * Prefers a value the caterer actually serves when that list is known.
 */
export function resolvePreferredServicingAirportId(input: {
  urlAirportId?: string | null;
  allowedAirportIds?: readonly (string | null | undefined)[];
}): string | undefined {
  const fromUrl = input.urlAirportId?.trim() || "";
  const fromBrowse = getBrowseServicingAirportId()?.trim() || "";
  const preferred = fromUrl || fromBrowse;
  const allowed = (input.allowedAirportIds ?? []).map((id) => id?.trim()).filter((id): id is string => Boolean(id));
  if (preferred && (allowed.length === 0 || allowed.includes(preferred))) {
    return preferred;
  }
  if (allowed.length === 1) return allowed[0];
  return undefined;
}

/** Append or replace `airport` on a path that may already have a query string. */
export function withServicingAirportQuery(path: string, airportId?: string | null): string {
  const id = airportId?.trim();
  if (!id) return path;
  const qIndex = path.indexOf("?");
  const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
  const params = new URLSearchParams(qIndex >= 0 ? path.slice(qIndex + 1) : "");
  params.set("airport", id);
  return `${base}?${params.toString()}`;
}

export type GuestBasketItem = {
  id: string;
  menu_item_id: string | null;
  name: string;
  price: number;
  quantity: number;
  notes?: string | null;
  image_url?: string | null;
  is_custom_item?: boolean;
  description?: string | null;
  custom_item_image_url?: string | null;
  category_name?: string | null;
};

export type GuestBasketState = {
  catererProfileId: string;
  catererName: string;
  currency: string;
  cancellationPolicy: string | null;
  displayPrices: boolean;
  basketNotes: string;
  items: GuestBasketItem[];
};

export type GuestCheckoutSnapshot = {
  airportId: string;
  deliveryDate: string;
  deliveryTime: string;
  deliveryTimezone: string;
  deliveryOption: CheckoutFormData["deliveryOption"];
  fbo: string;
  tailNumber: string;
  equipment: CheckoutFormData["equipment"] | null;
  registrationNumber: string | null;
  numberOfPassengers: number;
  adultPassengers: number;
  childPassengers: number;
  additionalNotes: string;
  notesToCaterer: string | null;
  flightPassengerContext: FlightPassengerContextV1;
  itemsTotal: number;
  stripeFee: number;
  basketNotes: string | null;
  cancellationPolicy: string | null;
  currency: string;
  pricesWereDisplayed: boolean;
};

export type GuestFirstOrderPackage = {
  version: 1;
  intent: typeof GUEST_FIRST_ORDER_INTENT;
  createdAt: string;
  updatedAt: string;
  preflight: PreflightOrderDraft | null;
  basket: GuestBasketState | null;
  checkoutSnapshot: GuestCheckoutSnapshot | null;
};

function emptyPackage(): GuestFirstOrderPackage {
  return {
    version: 1,
    intent: GUEST_FIRST_ORDER_INTENT,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    preflight: null,
    basket: null,
    checkoutSnapshot: null,
  };
}

function readRaw(): GuestFirstOrderPackage | null {
  try {
    const raw = localStorage.getItem(GUEST_FIRST_ORDER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as GuestFirstOrderPackage;
    if (!parsed || parsed.version !== 1 || parsed.intent !== GUEST_FIRST_ORDER_INTENT) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeRaw(pkg: GuestFirstOrderPackage): void {
  try {
    localStorage.setItem(
      GUEST_FIRST_ORDER_STORAGE_KEY,
      JSON.stringify({ ...pkg, updatedAt: new Date().toISOString() }),
    );
  } catch {
    /* ignore quota */
  }
}

/** Start / keep the guest first-order intent (call from marketing CTAs). */
export function startGuestFirstOrderIntent(): GuestFirstOrderPackage {
  const existing = readRaw();
  if (existing) {
    writeRaw(existing);
    return existing;
  }
  const pkg = emptyPackage();
  const draft = loadPreflightDraft();
  if (draft) pkg.preflight = draft;
  writeRaw(pkg);
  return pkg;
}

export function loadGuestFirstOrder(): GuestFirstOrderPackage | null {
  return readRaw();
}

export function saveGuestFirstOrder(pkg: GuestFirstOrderPackage): void {
  writeRaw(pkg);
}

export function clearGuestFirstOrder(): void {
  try {
    localStorage.removeItem(GUEST_FIRST_ORDER_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasGuestFirstOrderIntent(): boolean {
  return readRaw() != null;
}

/** True when URL intent or stored package means guest ordering UI should unlock. */
export function isGuestFirstOrderFlow(search?: string): boolean {
  if (hasGuestFirstOrderIntent()) return true;
  if (typeof search === "string") {
    try {
      const params = new URLSearchParams(search.startsWith("?") ? search : `?${search}`);
      if (params.get("intent") === GUEST_FIRST_ORDER_INTENT) return true;
    } catch {
      /* ignore */
    }
  }
  if (typeof window !== "undefined") {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get("intent") === GUEST_FIRST_ORDER_INTENT) return true;
    } catch {
      /* ignore */
    }
  }
  return false;
}

export function syncGuestPreflightFromSession(): void {
  const draft = loadPreflightDraft();
  const pkg = readRaw() ?? emptyPackage();
  pkg.preflight = draft;
  if (!pkg.createdAt) pkg.createdAt = new Date().toISOString();
  writeRaw(pkg);
  if (draft) savePreflightDraft(draft);
}

export function saveGuestBasket(basket: GuestBasketState): void {
  const pkg = readRaw() ?? emptyPackage();
  pkg.basket = basket;
  if (!pkg.preflight) pkg.preflight = loadPreflightDraft();
  writeRaw(pkg);
}

export function saveGuestCheckoutSnapshot(snapshot: GuestCheckoutSnapshot): void {
  const pkg = readRaw() ?? emptyPackage();
  pkg.checkoutSnapshot = snapshot;
  if (!pkg.preflight) pkg.preflight = loadPreflightDraft();
  writeRaw(pkg);
}

export function buildEquipmentPayload(
  equipment: CheckoutFormData["equipment"] | null | undefined,
): Record<string, unknown> | null {
  if (!equipment) return null;
  const hasAny =
    equipment.microwave ||
    equipment.fridge ||
    equipment.oven ||
    equipment.storage ||
    (equipment.other?.trim() ?? "") !== "";
  if (!hasAny) return null;
  const out: Record<string, unknown> = {};
  if (equipment.microwave) {
    out.microwave = true;
    if (equipment.microwave_dimensions?.trim()) {
      out.microwave_dimensions = equipment.microwave_dimensions.trim();
    }
  }
  if (equipment.fridge) {
    out.fridge = true;
    if (equipment.fridge_dimensions?.trim()) {
      out.fridge_dimensions = equipment.fridge_dimensions.trim();
    }
  }
  if (equipment.oven) {
    out.oven = true;
    if (equipment.oven_dimensions?.trim()) {
      out.oven_dimensions = equipment.oven_dimensions.trim();
    }
  }
  if (equipment.storage) {
    out.storage = true;
    if (equipment.storage_dimensions?.trim()) {
      out.storage_dimensions = equipment.storage_dimensions.trim();
    }
  }
  if (equipment.other?.trim()) out.other = equipment.other.trim();
  return out;
}

/** Body for create-order edge — mirrors authenticated Checkout submit. */
export function buildCreateOrderBodyFromGuestPackage(
  pkg: GuestFirstOrderPackage,
): Record<string, unknown> | null {
  const snap = pkg.checkoutSnapshot;
  const basket = pkg.basket;
  if (!snap || !basket || !basket.catererProfileId || basket.items.length === 0) {
    return null;
  }

  return {
    caterer_profile_id: basket.catererProfileId,
    airport_id: snap.airportId,
    delivery_date: snap.deliveryDate,
    delivery_time: snap.deliveryTime,
    delivery_timezone: snap.deliveryTimezone,
    delivery_option: snap.deliveryOption,
    fbo_name: snap.fbo,
    tail_number: snap.tailNumber.toUpperCase(),
    items: basket.items.map((item) => ({
      menu_item_id: item.menu_item_id,
      item_name: item.name,
      item_description: item.is_custom_item ? item.description || item.notes || null : null,
      item_price: item.price,
      quantity: item.quantity,
      notes: item.is_custom_item ? null : item.notes || null,
      is_custom_item: item.is_custom_item || false,
      custom_item_image_url: item.is_custom_item ? item.custom_item_image_url || null : null,
    })),
    basket_notes: (snap.basketNotes ?? basket.basketNotes) || null,
    cancellation_policy: snap.cancellationPolicy ?? basket.cancellationPolicy,
    currency: snap.currency || basket.currency || "USD",
    prices_were_displayed: snap.pricesWereDisplayed ?? basket.displayPrices,
    items_total: snap.itemsTotal,
    stripe_fee: snap.stripeFee ?? 0,
    equipment: buildEquipmentPayload(snap.equipment),
    registration_number: snap.registrationNumber?.trim() || null,
    number_of_passengers: snap.numberOfPassengers,
    notes_to_caterer: snap.notesToCaterer || null,
    flight_passenger_context: snap.flightPassengerContext,
  };
}

export function clearGuestFirstOrderAndDrafts(): void {
  clearGuestFirstOrder();
  clearPreflightDraft();
}

/**
 * Marketing Order now: create account → classic CFA onboarding → dashboard → order as usual.
 * Call `setBrowseServicingAirportId` separately when remembering a map airport.
 */
export function buildOrderNowSignupPath(): string {
  return appendUtmToPath(
    buildAuthPath({
      mode: "signup",
      redirect: ORDER_NOW_ONBOARDING_PATH,
      role: "pfa",
    }),
  );
}

/**
 * After guest checkout: signup then onboarding that places the saved first order
 * (basket + flight already captured in localStorage).
 */
export function buildOrderNowAuthRedirect(): string {
  return appendUtmToPath(
    buildAuthPath({
      mode: "signup",
      redirect: ONBOARDING_COMPLETE_FIRST_ORDER_PATH,
      role: "pfa",
    }),
  );
}
